'use client';

import HeroSection from './HeroSection';
import TrustSection from './TrustSection';
import ProcessSection from './ProcessSection';
import ProductShowcase from './ProductShowcase';
import CTABanner from './CTABanner';
import FooterWrapper from '@/components/common/FooterWrapper';
import { useLanguage } from '@/contexts/LanguageContext';

interface TrustBenefit {
  id: number;
  icon: string;
  title: string;
  description: string;
}

interface ProcessStep {
  id: number;
  stepNumber: number;
  icon: string;
  title: string;
  description: string;
}

interface ProductOption {
  id: number;
  icon: string;
  badge: string;
  title: string;
  description: string;
  priceLabel: string;
  priceAmount: string;
  features: string[];
  ctaText: string;
  ctaLink: string;
  highlighted?: boolean;
}

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  name: string;
  icon: string;
  href: string;
}

const HomeInteractive = () => {
  const { t } = useLanguage();

  // Hero Section Data
  const heroData = {
    headline: t('home.hero.headline'),
    subheadline: t('home.hero.subheadline'),
    quote: t('home.hero.quote'),
    quoteAuthor: t('home.hero.quoteAuthor')
  };

  // Trust Benefits Data
  const trustBenefits: TrustBenefit[] = [
    {
      id: 1,
      icon: "EyeIcon",
      title: t('home.trust.transparency.title'),
      description: t('home.trust.transparency.description')
    },
    {
      id: 2,
      icon: "ShieldCheckIcon",
      title: t('home.trust.quality.title'),
      description: t('home.trust.quality.description')
    },
    {
      id: 3,
      icon: "LockClosedIcon",
      title: t('home.trust.security.title'),
      description: t('home.trust.security.description')
    },
    {
      id: 4,
      icon: "UserGroupIcon",
      title: t('home.trust.consultation.title'),
      description: t('home.trust.consultation.description')
    }
  ];

  // Process Steps Data
  const processSteps: ProcessStep[] = [
    {
      id: 1,
      stepNumber: 1,
      icon: "ChatBubbleLeftRightIcon",
      title: t('home.process.step1.title'),
      description: t('home.process.step1.description')
    },
    {
      id: 2,
      stepNumber: 2,
      icon: "PhoneIcon",
      title: t('home.process.step2.title'),
      description: t('home.process.step2.description')
    },
    {
      id: 3,
      stepNumber: 3,
      icon: "TruckIcon",
      title: t('home.process.step3.title'),
      description: t('home.process.step3.description')
    }
  ];

  // Product Options Data
  const productOptions: ProductOption[] = [
    {
      id: 1,
      icon: "CalendarIcon",
      badge: t('home.products.savingsPlan.badge'),
      title: t('home.products.savingsPlan.title'),
      description: t('home.products.savingsPlan.description'),
      priceLabel: t('home.products.savingsPlan.priceLabel'),
      priceAmount: t('home.products.savingsPlan.priceAmount'),
      features: [
        t('home.products.savingsPlan.feature1'),
        t('home.products.savingsPlan.feature2'),
        t('home.products.savingsPlan.feature3'),
        t('home.products.savingsPlan.feature4'),
        t('home.products.savingsPlan.feature5')
      ],
      ctaText: t('home.products.savingsPlan.cta'),
      ctaLink: "/contact",
      highlighted: true
    },
    {
      id: 2,
      icon: "BanknotesIcon",
      badge: t('home.products.oneTime.badge'),
      title: t('home.products.oneTime.title'),
      description: t('home.products.oneTime.description'),
      priceLabel: t('home.products.oneTime.priceLabel'),
      priceAmount: t('home.products.oneTime.priceAmount'),
      features: [
        t('home.products.oneTime.feature1'),
        t('home.products.oneTime.feature2'),
        t('home.products.oneTime.feature3'),
        t('home.products.oneTime.feature4'),
        t('home.products.oneTime.feature5')
      ],
      ctaText: t('home.products.oneTime.cta'),
      ctaLink: "/contact",
      highlighted: false
    }
  ];

  // CTA Banner Data
  const ctaBannerData = {
    title: t('home.cta.title'),
    description: t('home.cta.description'),
    primaryCTA: {
      text: t('home.cta.button'),
      link: "/contact"
    },
    secondaryCTA: {
      text: t('common.viewProducts'),
      link: "/products"
    }
  };

  return (
    <>
      <HeroSection 
        headline={heroData.headline}
        subheadline={heroData.subheadline}
        quote={heroData.quote}
        quoteAuthor={heroData.quoteAuthor}
      />
      
      <TrustSection features={trustBenefits} />
      
      <ProcessSection steps={processSteps} />
      
      <ProductShowcase products={productOptions} />
      
      <CTABanner 
        title={ctaBannerData.title}
        description={ctaBannerData.description}
        primaryCTA={ctaBannerData.primaryCTA}
        secondaryCTA={ctaBannerData.secondaryCTA}
      />
      <FooterWrapper />
    </>
  );
};

export default HomeInteractive;